import { useState } from 'react';
import { toast } from 'react-toastify';
import { acceptGuestTerms } from '../../services/guestSessionService';
import Mixpanel from '../../utils/mixpanel';
import { Button, Dialog } from '../ui';

interface GuestTermsModalProps {
  open: boolean;
  onAccepted: () => void;
  onCancel: () => void;
}

const GuestTermsModal: React.FC<GuestTermsModalProps> = ({ open, onAccepted, onCancel }) => {
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleAccept = async () => {
    if (!checked || submitting) return;
    setSubmitting(true);
    try {
      await acceptGuestTerms();
      Mixpanel.track('guest_terms_accepted');
      setChecked(false);
      onAccepted();
    } catch (err) {
      toast.error('Could not record terms acceptance. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const trackLink = (doc: 'terms' | 'privacy') => () => {
    Mixpanel.track('guest_terms_link_clicked', { doc });
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(o) => {
        if (!o && !submitting) onCancel();
      }}
    >
      <Dialog.Content
        className="max-h-[90vh] max-w-[94vw] overflow-y-auto sm:max-w-[520px]"
        aria-describedby={undefined}
      >
        <div className="p-[26px_22px] font-sans sm:p-[30px]">
          <Dialog.Title className="mb-1.5 text-[22px] font-bold leading-[1.2] text-white sm:text-[26px]">
            Before your first upload
          </Dialog.Title>
          <p
            className="mb-[20px] text-[14.5px] leading-[1.7] sm:text-[15.5px]"
            style={{ color: 'rgba(255,255,255,0.82)' }}
          >
            Ommuse asks every guest to review the Terms of Use and Privacy Policy once, before
            uploading. You only need to do this on this session.
          </p>
          <label className="mb-[20px] flex cursor-pointer items-start gap-[11px] text-[14.5px] leading-[1.65] text-white">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              className="mt-[3px] h-[18px] w-[18px] flex-none cursor-pointer accent-[var(--cyan)]"
            />
            <span>
              I agree to the{' '}
              <a
                href={`${window.location.origin}/terms-of-use`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-cyan no-underline"
                onClick={trackLink('terms')}
              >
                Terms of Use
              </a>{' '}
              and{' '}
              <a
                href={`${window.location.origin}/privacy-policy`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-cyan no-underline"
                onClick={trackLink('privacy')}
              >
                Privacy Policy
              </a>
              .
            </span>
          </label>
          <div className="flex gap-[12px]">
            <Button variant="ghost" onClick={onCancel} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              fullWidth
              disabled={submitting || !checked}
              loading={submitting}
              onClick={handleAccept}
            >
              Agree and continue
            </Button>
          </div>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default GuestTermsModal;
